/**
 * The reading at the top of the visualizer dashboard and analytics overview:
 * what changed against the previous period, where people leave, and what to
 * do next — at most five lines, the actionable ones first after the headline.
 */
import { VIZ_LINKS } from './links';
import { ReadingItem, changePercent, sharePercent } from './reading';

export interface Pair {
  value: number | null | undefined;
  previous?: number | null;
}

export interface VizReadingInput {
  visitors: Pair;
  tries: Pair;
  downloads: Pair;
  leads: Pair;
  /** Distinct visitors per funnel step. */
  trialVisitors?: number | null;
  resultVisitors?: number | null;
  /** Hot and warm leads nobody has written to. */
  leadsWaiting?: number;
  hotWaiting?: number;
  /** The product most tried yet least kept, if one stands out. */
  weakProduct?: { name: string; tries: number; downloads: number } | null;
  topProduct?: { name: string; tries: number } | null;
  zeroResultSearches?: { count: number; topTerm?: string } | null;
  /** Upload photos of their own room: the strongest intent signal. */
  uploads?: Pair;
}

/** Below these a share is noise, not a finding. */
const MIN_VISITORS = 20;
const MIN_TRIERS = 10;
const MIN_SAVERS = 10;
const MOVE = 15; // % change worth saying

export function vizReading(input: VizReadingInput, max = 5): ReadingItem[] {
  const items: ReadingItem[] = [];
  const v = input.visitors.value ?? 0;
  const vp = input.visitors.previous ?? 0;

  // ── The headline: is it growing? ──────────────────────────────────────
  if (v > 0 || vp > 0) {
    const change = changePercent(v, vp);
    if (!vp && v > 0) {
      items.push({ id: 'visitors', tone: 'good', title: { key: 'VISITORS_FIRST', params: { count: v } } });
    } else if (change != null && change >= MOVE) {
      items.push({ id: 'visitors', tone: 'good', title: { key: 'VISITORS_UP', params: { count: v, percent: change } } });
    } else if (change != null && change <= -MOVE) {
      items.push({ id: 'visitors', tone: 'bad', title: { key: 'VISITORS_DOWN', params: { count: v, percent: Math.abs(change) } }, detail: { key: 'VISITORS_DOWN_DETAIL' } });
    } else {
      items.push({ id: 'visitors', tone: 'neutral', title: { key: 'VISITORS_STEADY', params: { count: v } } });
    }
  }

  // ── Leads waiting for an answer ───────────────────────────────────────
  const waiting = input.leadsWaiting ?? 0;
  if (waiting > 0) {
    const hot = input.hotWaiting ?? 0;
    items.push({
      id: 'leads-waiting',
      tone: 'watch',
      title: hot > 0 ? { key: 'HOT_LEADS_WAITING', params: { count: hot } } : { key: 'LEADS_WAITING', params: { count: waiting } },
      detail: { key: 'LEADS_WAITING_DETAIL' },
      action: { key: 'OPEN_LEADS', href: VIZ_LINKS.leadsWaiting },
    });
  }

  // ── Where people leave: the first weak step, once there is enough traffic ─
  const tried = input.trialVisitors ?? null;
  const saved = input.resultVisitors ?? null;
  const newLeads = input.leads.value ?? null;
  const tryShare = sharePercent(tried, v, MIN_VISITORS);
  const saveShare = sharePercent(saved, tried, MIN_TRIERS);
  const leadShare = sharePercent(newLeads, saved, MIN_SAVERS);
  if (tryShare != null && tryShare < 25) {
    items.push({
      id: 'funnel',
      tone: 'watch',
      title: { key: 'FUNNEL_FEW_TRY', params: { percent: tryShare } },
      detail: { key: 'FUNNEL_FEW_TRY_DETAIL' },
      action: { key: 'PRODUCT_DISPLAY', href: VIZ_LINKS.productDisplay },
    });
  } else if (saveShare != null && saveShare < 10) {
    items.push({
      id: 'funnel',
      tone: 'watch',
      title: { key: 'FUNNEL_FEW_SAVE', params: { percent: saveShare } },
      detail: { key: 'FUNNEL_FEW_SAVE_DETAIL' },
      action: { key: 'FEATURES', href: VIZ_LINKS.features },
    });
  } else if (leadShare != null && leadShare < 20) {
    items.push({
      id: 'funnel',
      tone: 'watch',
      title: { key: 'FUNNEL_FEW_LEADS', params: { percent: leadShare } },
      detail: { key: 'FUNNEL_FEW_LEADS_DETAIL' },
      action: { key: 'FEATURES', href: VIZ_LINKS.features },
    });
  }

  // ── A product people try but do not keep ──────────────────────────────
  if (input.weakProduct) {
    items.push({
      id: 'weak-product',
      tone: 'watch',
      title: { key: 'PRODUCT_TRIED_NOT_SAVED', params: { name: input.weakProduct.name } },
      detail: { key: 'PRODUCT_TRIED_NOT_SAVED_DETAIL', params: { tries: input.weakProduct.tries, count: input.weakProduct.downloads } },
      action: { key: 'SEE_PRODUCTS', href: VIZ_LINKS.products },
    });
  }

  // ── Searches that found nothing ───────────────────────────────────────
  const zero = input.zeroResultSearches;
  if (zero && zero.count > 0) {
    items.push({
      id: 'zero-searches',
      tone: 'watch',
      title: { key: 'ZERO_SEARCHES', params: { count: zero.count } },
      detail: zero.topTerm ? { key: 'ZERO_SEARCHES_DETAIL', params: { term: zero.topTerm } } : undefined,
      action: { key: 'SEE_SEARCHES', href: VIZ_LINKS.searches },
    });
  }

  // ── What is working ───────────────────────────────────────────────────
  const l = input.leads.value ?? 0;
  const lp = input.leads.previous ?? 0;
  const leadChange = changePercent(l, lp);
  if (l > 0 && !lp) {
    items.push({ id: 'leads', tone: 'good', title: { key: 'LEADS_NEW', params: { count: l } } });
  } else if (leadChange != null && Math.abs(leadChange) >= 20) {
    items.push({
      id: 'leads',
      tone: leadChange > 0 ? 'good' : 'bad',
      title: { key: leadChange > 0 ? 'LEADS_UP' : 'LEADS_DOWN', params: { count: l, percent: Math.abs(leadChange) } },
    });
  }

  const d = input.downloads.value ?? 0;
  const dp = input.downloads.previous ?? 0;
  const downloadChange = changePercent(d, dp);
  if (downloadChange != null && dp >= 5 && Math.abs(downloadChange) >= 20) {
    items.push({
      id: 'downloads',
      tone: downloadChange > 0 ? 'good' : 'bad',
      title: { key: downloadChange > 0 ? 'DOWNLOADS_UP' : 'DOWNLOADS_DOWN', params: { count: d, percent: Math.abs(downloadChange) } },
    });
  }

  if (input.topProduct && input.topProduct.tries > 0) {
    items.push({ id: 'top-product', tone: 'good', title: { key: 'TOP_PRODUCT', params: { name: input.topProduct.name, count: input.topProduct.tries } } });
  }

  const uploads = input.uploads?.value ?? 0;
  if (uploads > 0) {
    items.push({ id: 'uploads', tone: 'good', title: { key: 'UPLOADS', params: { count: uploads } }, detail: { key: 'UPLOADS_DETAIL' } });
  }

  return items.slice(0, max);
}
